import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Service } from '@angular/core';

/** Body classes that trigger document scroll lock (see layout SCSS `:has()` rules). */
const LOCK_CLASSES = ['p-overflow-hidden', 'blocked-scroll'] as const;

/**
 * Measures the device scrollbar width via an offscreen probe element.
 * Returns `0` on overlay-scrollbar systems (macOS, mobile) where no gutter
 * compensation is needed.
 */
export function measureDeviceScrollbarWidth(doc: Document): number {
  const outer = doc.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.width = '100px';
  outer.style.height = '100px';
  outer.style.position = 'absolute';
  outer.style.top = '-9999px';

  doc.body.append(outer);

  const inner = doc.createElement('div');
  inner.style.width = '100%';
  inner.style.height = '200px';
  outer.append(inner);

  const width = outer.offsetWidth - outer.clientWidth;
  outer.remove();

  return width;
}

/**
 * Reserves the vertical scrollbar gutter on `html` only while a scroll lock is
 * active **and** the page had a real classic scrollbar before locking.
 *
 * CSS `:has()` rules in layout SCSS apply `overflow: hidden` on lock; this
 * service adds `scrollbar-gutter: stable` inline so fixed chrome (the topbar)
 * and `vw`-sized dialogs do not shift when the scrollbar disappears. Short,
 * full-bleed, and fullscreen pages skip reservation because they were not
 * scrollable — no empty gutter strip when idle.
 *
 * Initialise once from {@link LayoutComponent} via {@link ScrollLockService.init}.
 */
@Service()
export class ScrollLockService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  private deviceScrollbarWidth = 0;
  private gutterReserved = false;
  private observer: MutationObserver | null = null;

  /**
   * Starts observing `body` class changes for scroll-lock hooks. Safe to call
   * once per app lifetime; subsequent calls are ignored.
   */
  init(): void {
    if (this.document.defaultView === null) {
      return;
    }
    if (this.observer) {
      return;
    }

    this.deviceScrollbarWidth = measureDeviceScrollbarWidth(this.document);

    this.observer = new MutationObserver(() => {
      this.syncGutter();
    });
    this.observer.observe(this.document.body, {
      attributeFilter: ['class'],
      attributes: true,
    });

    this.destroyRef.onDestroy(() => {
      this.observer?.disconnect();
      this.observer = null;
      this.clearGutterReservation();
    });

    this.syncGutter();
  }

  private clearGutterReservation(): void {
    if (!this.gutterReserved) {
      return;
    }
    this.document.documentElement.style.removeProperty('scrollbar-gutter');
    this.gutterReserved = false;
  }

  private isScrollLocked(): boolean {
    return LOCK_CLASSES.some((lockClass) =>
      this.document.body.classList.contains(lockClass)
    );
  }

  private reserveGutterIfNeeded(): void {
    if (this.gutterReserved) {
      return;
    }
    if (this.deviceScrollbarWidth <= 0) {
      return;
    }
    if (!this.wasScrollable()) {
      return;
    }

    this.document.documentElement.style.scrollbarGutter = 'stable';
    this.gutterReserved = true;
  }

  private syncGutter(): void {
    if (this.isScrollLocked()) {
      this.reserveGutterIfNeeded();
    } else {
      this.clearGutterReservation();
    }
  }

  private wasScrollable(): boolean {
    const { documentElement } = this.document;
    return documentElement.scrollHeight > documentElement.clientHeight;
  }
}
